import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteConfig, categories } from '@/mocks/siteData';
import { getComicsByCategory, ComicArticle } from '@/mocks/comics';

function ComicCard({ comic }: { comic: ComicArticle }) {
  return (
    <Link
      to={`/article/${comic.slug}`}
      className="group block bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-300/50 transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={comic.coverImage}
          alt={`${comic.title} - 韩国漫画`}
          title={`${comic.title} - 好看的韩国漫画免费在线观看`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-2 left-2">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
            comic.status === '连载中'
              ? 'bg-accent-500 text-background-50'
              : 'bg-secondary-500 text-background-50'
          }`}>
            {comic.status}
          </span>
        </div>
        <div className="absolute top-2 right-2 flex items-center gap-1 bg-background-950/70 text-background-50 text-xs px-2 py-0.5 rounded-full">
          <i className="ri-star-fill text-secondary-400 text-xs"></i>
          <span>{comic.rating}</span>
        </div>
      </div>
      <div className="p-3">
        <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1 font-heading">
          {comic.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-1 line-clamp-1">{comic.author}</p>
        <p className="text-xs text-foreground-400 mt-1.5 line-clamp-2 leading-relaxed">{comic.description}</p>
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-foreground-400">
            <i className="ri-eye-line mr-1"></i>
            {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views}
          </span>
          <span className="text-xs text-foreground-400">
            {comic.chapters}话
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const activeSlug = slug || 'all';
  const currentCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const comics = getComicsByCategory(activeSlug);

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - ${siteConfig.name}`,
    description: `${siteConfig.name}为您提供精选${currentCategory.name}类韩国漫画，全部免费在线观看。探索海量优质韩漫作品，尽在韩漫网。`,
    url: `${siteConfig.url}/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteConfig.name,
      url: siteConfig.url,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteConfig.url },
      { '@type': 'ListItem', position: 2, name: '分类浏览', item: `${siteConfig.url}/category/all` },
      { '@type': 'ListItem', position: 3, name: currentCategory.name },
    ],
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-4">
        <Breadcrumb
          items={[
            { label: '分类浏览', href: '/category/all' },
            { label: currentCategory.name },
          ]}
        />

        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
            {currentCategory.name}漫画
          </h1>
          <p className="text-sm text-foreground-500 mt-2">
            {currentCategory.slug === 'all'
              ? '浏览全部韩国漫画，发现你最爱的作品'
              : `精选${currentCategory.name}类韩国漫画，全部免费在线观看`}
            · 共 <strong className="text-primary-600">{comics.length}</strong> 部作品
          </p>
        </div>

        <div className="flex items-center gap-2 overflow-x-auto pb-3 flex-wrap">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                activeSlug === cat.slug
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              <i className={`${cat.icon} text-sm`}></i>
              {cat.name}
            </Link>
          ))}
        </div>
      </div>

      <section className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {comics.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
            {comics.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-foreground-400 text-2xl"></i>
            </div>
            <p className="mt-4 text-foreground-500">该分类下暂无漫画内容</p>
            <Link to="/category/all" className="inline-block mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium">
              查看全部漫画
            </Link>
          </div>
        )}
      </section>
    </Layout>
  );
}